import { useEffect, useState } from "react";
import { api } from "../api/client";
import { b64decode, decryptItem, unwrapItemKey } from "../crypto/vault";
import type { ItemContent } from "../vault/items";

interface VersionRow {
  versionNo: number;
  name: string;
  createdAt?: string;
}

// Shows an item's encrypted version snapshots (§26), decrypted in the browser, with
// restore. The same item_key decrypts every version (content edits keep the key).
export function VersionHistory({
  itemId,
  orgKey,
  onClose,
  onRestored,
}: {
  itemId: string;
  orgKey: Uint8Array;
  onClose: () => void;
  onRestored: () => void;
}) {
  const [rows, setRows] = useState<VersionRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const summary = await api.getItem(itemId);
        const itemKey = unwrapItemKey(orgKey, b64decode(summary.wrapped_item_key));
        const { versions } = await api.listVersions(itemId);
        const out = versions.map((v) => {
          let name = "(unreadable)";
          try {
            name = decryptItem<ItemContent>(itemKey, b64decode(v.enc_content)).name;
          } catch {
            /* keep placeholder */
          }
          return { versionNo: v.version_no, name, createdAt: v.created_at };
        });
        if (alive) setRows(out);
      } catch {
        if (alive) setError("Could not load history");
      }
    })();
    return () => {
      alive = false;
    };
  }, [itemId, orgKey]);

  async function restore(versionNo: number) {
    setBusy(true);
    try {
      await api.restoreVersion(itemId, versionNo);
      onRestored();
    } catch {
      setError("Restore failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 grid place-items-center bg-black/50" onClick={onClose}>
      <div className="w-[440px] rounded-md border border-border bg-panel shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="border-b border-border px-4 py-3 font-semibold">Version history</div>
        <div className="max-h-[50vh] overflow-auto px-4 py-3 text-sm">
          {error && <div className="text-xs text-danger">{error}</div>}
          {rows.length === 0 && !error && <div className="text-xs text-muted">No previous versions.</div>}
          {rows.map((v) => (
            <div key={v.versionNo} className="flex items-center justify-between border-b border-border py-1.5">
              <div>
                <div>v{v.versionNo} · {v.name}</div>
                <div className="text-xs text-muted">{v.createdAt?.slice(0, 19).replace("T", " ")}</div>
              </div>
              <button
                onClick={() => restore(v.versionNo)}
                disabled={busy}
                className="rounded border border-border px-2 py-1 text-xs hover:border-accent disabled:opacity-60"
              >
                Restore
              </button>
            </div>
          ))}
        </div>
        <div className="flex justify-end border-t border-border px-4 py-3">
          <button onClick={onClose} className="rounded border border-border px-3 py-1.5 hover:border-accent">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
